import { Link } from 'react-router-dom'
import { HalamanLegal, Pasal, Surel } from '../components/HalamanLegal'

/**
 * Kebijakan privasi orisini.com — berlaku untuk seluruh produk Orisini.
 *
 * Isinya harus mengikuti apa yang kode BENAR-BENAR lakukan. Kalau menambah
 * penyedia pihak ketiga, menyimpan data baru, atau mengubah apa yang dikirim
 * agent ke server (mis. screenshot kegagalan), perbarui pasal terkait dan
 * `BERLAKU` di HalamanLegal.tsx.
 */
export default function PrivacyPage() {
  return (
    <HalamanLegal
      judul="Privacy Policy"
      ringkas="This policy explains what Orisini stores, where it is stored, and who else processes it. It covers orisini.com, every Orisini product subdomain (such as pinboss.orisini.com), the Orisini Agent desktop app, and the Orisini browser extensions."
    >
      <Pasal id="short-version" judul="The short version">
        <ul>
          <li>
            <strong>Your social media passwords never reach our servers.</strong> You sign in to
            each platform inside a Chrome profile on your own computer, and that session stays there.
          </li>
          <li>We store your account, your content, your schedule, and the results of each post.</li>
          <li>When a post fails, the agent may upload a screenshot of the page so the failure can be diagnosed.</li>
          <li>We don't sell your data, show ads, or use third-party analytics or tracking cookies.</li>
        </ul>
      </Pasal>

      <Pasal id="what-we-store" judul="1. What we store on our servers">
        <p>
          <strong>Account.</strong> Your email address, name, a salted hash of your password (never
          the password itself), whether your email is verified, and when you accepted these terms.
        </p>
        <p>
          <strong>Sessions and devices.</strong> Active sign-in sessions, plus a record of each
          computer running the agent and each installed extension: device name, operating system,
          agent version, and when it was last seen. Device and extension tokens are stored only as
          a SHA-256 hash.
        </p>
        <p>
          <strong>Connected platform accounts.</strong> The handle or username of each social media
          account you connect (for example your Pinterest username), its boards or profile details
          needed for posting, and which device it lives on. Not its password or login cookies.
        </p>
        <p>
          <strong>Content.</strong> Images, videos, audio, text, captions, templates, brand
          settings, topics, and drafts you create or upload, along with the rendered results
          (posters, carousels, videos).
        </p>
        <p>
          <strong>Schedule and history.</strong> What is queued, when it is due, whether it was
          published, the resulting post URL, and error details when it wasn't.
        </p>
        <p>
          <strong>Failure diagnostics.</strong> When a post fails, the agent may capture a screenshot
          and the page markup of the platform tab at that moment and upload them to us. These can
          show whatever was visible on that page, including parts of your feed or account.
        </p>
        <p>
          <strong>Billing.</strong> Your plan, subscription dates, orders, amounts, and any payment
          proof you upload. Payment is made via QRIS through your own bank or e-wallet; we never
          receive your card or account numbers.
        </p>
        <p>
          <strong>Usage.</strong> Counts of posts, connected accounts, and generated content per
          month, used to apply plan limits, and a log of AI requests (purpose, model, token counts,
          success) used for quotas and cost accounting.
        </p>
      </Pasal>

      <Pasal id="on-your-computer" judul="2. What stays on your computer">
        <p>
          The Orisini Agent keeps a dedicated Google Chrome profile on your computer. Your sign-ins
          to Pinterest, Instagram, and other platforms live in that profile, as do the cookies those
          platforms set. We do not upload that profile. Signing out of a platform, or uninstalling
          the agent and deleting its data folder, removes those sessions.
        </p>
        <p>
          The agent also stores its own device token and settings locally, and may render long
          videos on your computer instead of on our servers.
        </p>
      </Pasal>

      <Pasal id="how-we-use" judul="3. How we use it">
        <ul>
          <li>to run the service: sign you in, keep your schedule, send jobs to your agent, and render content;</li>
          <li>to apply plan limits and process subscriptions;</li>
          <li>to send account emails: verification, password reset, and service notices;</li>
          <li>to diagnose failures and keep the service working as platforms change;</li>
          <li>to prevent abuse and secure accounts.</li>
        </ul>
        <p>
          We don't send marketing email without asking, and we don't use your content to train AI
          models.
        </p>
      </Pasal>

      <Pasal id="third-parties" judul="4. Who else processes your data">
        <p>Only the providers needed to run Orisini, each for the purpose listed:</p>
        <ul>
          <li>
            <strong>Cloudflare</strong> — hosting, databases, file storage, rendering, and some AI
            models (including text-to-speech and speech-to-text). All server-side data lives here.
          </li>
          <li>
            <strong>Google (Gemini)</strong> — reading images and generating content or voice when
            you use AI features. The image or text involved is sent to Google.
          </li>
          <li>
            <strong>DeepSeek</strong> — generating text such as headlines and captions when you use
            AI features. The prompt and source text are sent to DeepSeek.
          </li>
          <li>
            <strong>ElevenLabs</strong> — generating voice-overs when that voice is selected. The
            script text is sent to ElevenLabs.
          </li>
          <li>
            <strong>Brevo</strong> — delivering account emails. Your email address and the email
            content are sent to Brevo.
          </li>
          <li>
            <strong>The social platforms you post to</strong> — the agent publishes your content to
            them from your own computer, under their own privacy policies.
          </li>
        </ul>
        <p>
          AI providers receive only what a given request needs, never your account password or
          platform logins. Some of these providers process data outside Indonesia.
        </p>
        <p>We disclose data to authorities only when legally required to.</p>
      </Pasal>

      <Pasal id="cookies" judul="5. Cookies">
        <p>
          We set one essential cookie: your sign-in session, shared across orisini.com and its
          product subdomains so one sign-in works everywhere. It lasts up to 30 days and is removed
          when you sign out. There are no advertising or analytics cookies. Your browser may also
          keep small interface preferences in local storage.
        </p>
      </Pasal>

      <Pasal id="retention" judul="6. How long we keep it">
        <ul>
          <li>Account data: as long as your account exists.</li>
          <li>Content, schedule, and post history: until you delete them or your account.</li>
          <li>Failure screenshots and page markup: only as long as they are useful for diagnosis.</li>
          <li>
            Billing records: kept after account deletion where tax or accounting obligations require
            it.
          </li>
          <li>Articles collected for Discover from public feeds: about three days.</li>
        </ul>
      </Pasal>

      <Pasal id="your-rights" judul="7. Your choices and rights">
        <p>
          You can view and edit your profile in Settings, revoke devices and sessions in{' '}
          <Link to="/devices" className="text-foreground underline underline-offset-4">
            Devices
          </Link>
          , and delete content and connected accounts from inside the app.
        </p>
        <p>
          To get a copy of your data, correct it, or delete your account and everything in it,
          email <Surel /> from the address on your account. We'll respond within 30 days. These
          rights apply regardless of where you live, including under Indonesia's Personal Data
          Protection Law (UU PDP).
        </p>
      </Pasal>

      <Pasal id="security" judul="8. Security">
        <p>
          Connections are encrypted with HTTPS. Passwords are hashed with PBKDF2; device and
          extension tokens are hashed; platform API keys we hold are encrypted at rest. Every
          request is checked against the owner of the data it touches. No system is perfectly
          secure — if we learn of a breach affecting your data, we'll tell you without undue delay.
        </p>
      </Pasal>

      <Pasal id="children" judul="9. Children">
        <p>Orisini is not intended for anyone under 18, and we don't knowingly collect their data.</p>
      </Pasal>

      <Pasal id="changes" judul="10. Changes">
        <p>
          When this policy changes, the effective date above changes too. If a change is
          significant, we'll email you before it takes effect.
        </p>
      </Pasal>

      <Pasal id="contact" judul="11. Contact">
        <p>
          Questions about privacy: <Surel />.
        </p>
      </Pasal>
    </HalamanLegal>
  )
}
